import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { ArrowIcon, Button as Button95, Checkbox, Hourglass, NumberInput, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { CommunityMembership } from "@/types/community_membership";
import { RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import { Meta } from '@/components/Meta';
import { Image } from 'expo-image';
import { Religion, ReligionNames } from '@/types/profile';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: '100%',
  },
  inputWrapper: {
    gap: 10,
    alignItems: 'center',
    width: '100%',
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
    minWidth: AppConsts.BASIC_MINIMUM_WIDTH,
    maxWidth: AppConsts.MAX_WIDTH_COMFORTABLE_READING,
  },
  numericWrapper: {
    flexDirection: 'row',
    gap: 5,
  },
  numeric: {
    width: 80,
  },
  flexRow: {
    flexDirection: "row",
    gap: 10,
    alignItems: "center",
    alignSelf: "flex-start",
    flexWrap: "wrap",
  },
});

export default function ReligionScreen() {
  const domain = useSelector((state: RootState) => state.user.domain);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const router = useRouter();
  const dispatch = useDispatch();
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const { edit } = useLocalSearchParams<{ edit?: string }>();

  const [religionAgnostic, setReligionAgnostic] = useState(AppUtils.isBitSet(Religion.RELIGION_AGNOSTIC, communityMembership.profile.religion));
  const [religionAtheist, setReligionAtheist] = useState(AppUtils.isBitSet(Religion.RELIGION_ATHEIST, communityMembership.profile.religion));
  const [religionBuddhist, setReligionBuddhist] = useState(AppUtils.isBitSet(Religion.RELIGION_BUDDHIST, communityMembership.profile.religion));
  const [religionCatholic, setReligionCatholic] = useState(AppUtils.isBitSet(Religion.RELIGION_CATHOLIC, communityMembership.profile.religion));
  const [religionChristian, setReligionChristian] = useState(AppUtils.isBitSet(Religion.RELIGION_CHRISTIAN, communityMembership.profile.religion));
  const [religionHindu, setReligionHindu] = useState(AppUtils.isBitSet(Religion.RELIGION_HINDU, communityMembership.profile.religion));
  const [religionJewish, setReligionJewish] = useState(AppUtils.isBitSet(Religion.RELIGION_JEWISH, communityMembership.profile.religion));
  const [religionMuslim, setReligionMuslim] = useState(AppUtils.isBitSet(Religion.RELIGION_MUSLIM, communityMembership.profile.religion));
  const [religionSikh, setReligionSikh] = useState(AppUtils.isBitSet(Religion.RELIGION_SIKH, communityMembership.profile.religion));
  const [religionSpiritual, setReligionSpiritual] = useState(AppUtils.isBitSet(Religion.RELIGION_SPIRITUAL, communityMembership.profile.religion));
  const [religionOther, setReligionOther] = useState(AppUtils.isBitSet(Religion.RELIGION_OTHER, communityMembership.profile.religion));

  const onSubmit = useCallback(
    async () => {
      try {
        showSpinner();

        const response = await AppUtilities.fetchAuthorized(
          "ReligionScreen.onSubmit",
          "/rabbl/api/optionalsettings",
          "POST",
          {
            domain: domain,
            religion: 1,
            religionAgnostic: religionAgnostic,
            religionAtheist: religionAtheist,
            religionBuddhist: religionBuddhist,
            religionCatholic: religionCatholic,
            religionChristian: religionChristian,
            religionHindu: religionHindu,
            religionJewish: religionJewish,
            religionMuslim: religionMuslim,
            religionSikh: religionSikh,
            religionSpiritual: religionSpiritual,
            religionOther: religionOther,
          }
        );
        const membership: CommunityMembership = ((response as any).body as any).community_membership;
        dispatch(setData(response));
        if (edit && edit.length > 0 && router.canGoBack()) {
          router.back();
        } else {
          router.navigate(AppUtils.getProfileCreateNextOptionalPageLink(membership) as RelativePathString);
        }

      } catch (e) {
        AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(), true);
        AppUtilities.handleException(e, "ReligionScreen.onSubmit");
      } finally {
        hideSpinner();
      }
    },
    [
      router,
      domain,
      religionAgnostic,
      religionAtheist,
      religionBuddhist,
      religionCatholic,
      religionChristian,
      religionHindu,
      religionJewish,
      religionMuslim,
      religionSikh,
      religionSpiritual,
      religionOther,
    ]
  );

  return (
    <BasicScrollView center={true}>
      <Meta title={"Religion"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>

          <Checkbox
            status={religionAgnostic ? 'checked' : 'unchecked'}
            onPress={() => setReligionAgnostic(religionAgnostic ? false : true)}
            label={ReligionNames.get(Religion.RELIGION_AGNOSTIC)}
          />

          <Checkbox
            status={religionAtheist ? 'checked' : 'unchecked'}
            onPress={() => setReligionAtheist(religionAtheist ? false : true)}
            label={ReligionNames.get(Religion.RELIGION_ATHEIST)}
          />

          <Checkbox
            status={religionBuddhist ? 'checked' : 'unchecked'}
            onPress={() => setReligionBuddhist(religionBuddhist ? false : true)}
            label={ReligionNames.get(Religion.RELIGION_BUDDHIST)}
          />

          <Checkbox
            status={religionCatholic ? 'checked' : 'unchecked'}
            onPress={() => setReligionCatholic(religionCatholic ? false : true)}
            label={ReligionNames.get(Religion.RELIGION_CATHOLIC)}
          />

          <Checkbox
            status={religionChristian ? 'checked' : 'unchecked'}
            onPress={() => setReligionChristian(religionChristian ? false : true)}
            label={ReligionNames.get(Religion.RELIGION_CHRISTIAN)}
          />

          <Checkbox
            status={religionHindu ? 'checked' : 'unchecked'}
            onPress={() => setReligionHindu(religionHindu ? false : true)}
            label={ReligionNames.get(Religion.RELIGION_HINDU)}
          />

          <Checkbox
            status={religionJewish ? 'checked' : 'unchecked'}
            onPress={() => setReligionJewish(religionJewish ? false : true)}
            label={ReligionNames.get(Religion.RELIGION_JEWISH)}
          />

          <Checkbox
            status={religionMuslim ? 'checked' : 'unchecked'}
            onPress={() => setReligionMuslim(religionMuslim ? false : true)}
            label={ReligionNames.get(Religion.RELIGION_MUSLIM)}
          />

          <Checkbox
            status={religionSikh ? 'checked' : 'unchecked'}
            onPress={() => setReligionSikh(religionSikh ? false : true)}
            label={ReligionNames.get(Religion.RELIGION_SIKH)}
          />

          <Checkbox
            status={religionSpiritual ? 'checked' : 'unchecked'}
            onPress={() => setReligionSpiritual(religionSpiritual ? false : true)}
            label={ReligionNames.get(Religion.RELIGION_SPIRITUAL)}
          />

          <Checkbox
            status={religionOther ? 'checked' : 'unchecked'}
            onPress={() => setReligionOther(religionOther ? false : true)}
            label={ReligionNames.get(Religion.RELIGION_OTHER)}
          />

          <View style={styles.buttonWrapper}>
            <Button95
              onPress={onSubmit}
            >{ edit ? "Save" : "Next" }</Button95>
          </View>
        </View>
      </Panel>
      
      <Footer />
    </BasicScrollView>
  );
}
